"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { signIn } from "next-auth/react";
import { ArrowRight, Check, Loader2 } from "lucide-react";

import { cn } from "@/app/_lib/utils";
import { submitContactEnquiry } from "../lib/actions";
import { trackEvent } from "@/app/_lib/analytics";
import { FormError } from "@/app/_components/form-error";
import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from "@/app/_components/turnstile-widget";

const NEEDS = [
  "Web App Development",
  "Mobile App Development",
  "Dedicated Product Team",
  "Product Care",
];
const BUDGETS = ["Under $10k", "$10k – $25k", "$25k – $60k", "$60k plus"];

export function ContactForm() {
  const router = useRouter();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [website, setWebsite] = React.useState("");
  const [needs, setNeeds] = React.useState<string[]>([]);
  const [budget, setBudget] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  // Bot mitigation: Turnstile token, honeypot ("website" is already a real
  // field on this form, so the honeypot uses a different name), and a render
  // timestamp to reject implausibly-fast submissions.
  const [turnstileToken, setTurnstileToken] = React.useState("");
  const [honeypot, setHoneypot] = React.useState("");
  const turnstileRef = React.useRef<TurnstileWidgetHandle>(null);
  const formRenderedAtRef = React.useRef(Date.now());

  const toggleNeed = (n: string) =>
    setNeeds((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n],
    );

  const chip = (active: boolean) =>
    cn(
      "rounded-full bg-white  border px-3.5 py-1.5 text-xs font-medium transition-colors",
      active
        ? "border-primary bg-white text-primary"
        : "border-border text-muted-foreground hover:text-foreground",
    );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please add your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    const [firstName, ...rest] = name.trim().split(/\s+/);
    const result = await submitContactEnquiry(
      {
        firstName,
        lastName: rest.join(" ") || "",
        email,
        company: company || undefined,
        website: website || undefined,
        needs,
        budget: budget || undefined,
        message,
        turnstileToken,
        honeypot,
        formRenderedAt: formRenderedAtRef.current,
      },
      typeof window !== "undefined" ? window.location.origin : "",
    );

    if (!result.success || !result.feedbackId) {
      setError(
        (result as { message?: string }).message ||
          "Something went wrong. Please try again.",
      );
      setSubmitting(false);
      turnstileRef.current?.reset();
      return;
    }

    trackEvent("form_submit", { form_name: "contact_us" });
    const destination = `/dashboard/messages/${result.feedbackId}`;
    setDone(true);

    if (result.loggedIn) {
      router.push(destination);
      router.refresh();
      return;
    }
    const token = (result as { autoLoginToken?: string }).autoLoginToken;
    if (token) {
      const res = await signIn("guest-token", { token, redirect: false });
      if (res?.ok) {
        if (typeof window !== "undefined")
          localStorage.setItem("needsProfileSetup", "true");
        router.push(destination);
        router.refresh();
        return;
      }
    }
    // New user — set-password email sent; still route to the chat.
    router.push(destination);
    router.refresh();
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl bg-mint p-10 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-primary text-white">
          <Check size={24} />
        </span>
        <h3 className="font-serif text-xl text-foreground">Message sent.</h3>
        <p className="max-w-sm text-sm text-muted-foreground">
          Taking you to your conversation. We&apos;ll reply within 12 hours.
        </p>
      </div>
    );
  }

  const input =
    "h-11 w-full rounded-none border border-border bg-background px-3.5 text-sm outline-none focus:border-primary/50";

  return (
    <form
      onSubmit={onSubmit}
      className=" bg-[#DDECCF33] border border-primary shadow-[0px_0px_25px_0px_#0000001A] p-6 md:p-8"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">
            Your name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className={input}
            style={{ fontSize: "16px" }}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className={input}
            style={{ fontSize: "16px" }}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">
            Company
          </label>
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={input}
            style={{ fontSize: "16px" }}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">
            Website (optional)
          </label>
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://"
            className={input}
            style={{ fontSize: "16px" }}
          />
        </div>
      </div>

      <div className="mt-5">
        <p className="mb-2 text-xs text-muted-foreground">
          What do you need help with?
        </p>
        <div className="flex flex-wrap gap-2">
          {NEEDS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => toggleNeed(n)}
              className={chip(needs.includes(n))}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <p className="mb-2 text-xs text-muted-foreground">Approximate budget</p>
        <div className="flex flex-wrap gap-2">
          {BUDGETS.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBudget(budget === b ? "" : b)}
              className={chip(budget === b)}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <label className="mb-1.5 block text-xs text-muted-foreground">
          Tell us about the project
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={8}
          placeholder="A few sentences on what you're building, who it's for, and what a good outcome looks like."
          className="w-full  border border-border bg-background p-3.5 text-sm outline-none focus:border-primary/50 resize-none"
          style={{ fontSize: "16px" }}
        />
      </div>

      {/* Honeypot — hidden via CSS (not type="hidden") so bots that fill every
          field in the DOM still trip it. Real users never see or fill it. */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="middleName">Middle name</label>
        <input
          id="middleName"
          name="middleName"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} />

      {error && (
        <div className="mt-3">
          <FormError message={error} />
        </div>
      )}

      <div className="mt-5 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <p className="max-w-xs text-[11px] leading-relaxed text-muted-foreground">
          By sending, you agree to our{" "}
          <a
            href="/privacy-policy"
            className="text-primary underline transition-opacity hover:opacity-70"
          >
            privacy policy
          </a>
          . We never share your details or reply with spam.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              Send message <ArrowRight size={15} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
